// ---------------------------------------------------------------------------
// F3c C1 — trocas de série (timeframe/ativo) medidas por etapa. Só diagnóstico, só com "?dbg=1"; NÃO altera nada.
//
// Etapas, em ms desde o início da troca (o efeito da série nova, logo depois da escolha na roleta):
//   pedidos     : pedidos de velas enviados (quantos, peso no limitador do servidor, caminho: grade | reserva | presente)
//   1ª resposta : o primeiro pedido que voltou; última: o último dos necessários para a primeira vista
//   vista       : série nova entregue com a vela de T no centro (Δ = velas entre o centro e a vela que contém T)
//   quadro      : o primeiro quadro desenhado depois dessa entrega
//   completa    : T ± 1.000 velas carregadas (ou até o início/presente da fonte)
//   pré-carga   : a primeira carga em segundo plano depois da troca
//   força       : quanto a força da região visível mudou quando o cálculo foi refeito com mais velas (máx e média, em %
//                 da faixa da força visível na primeira vista; meta <= 7%)
// Nada disso roda por quadro: só nos eventos da troca (alguns por troca).
// ---------------------------------------------------------------------------

import { DBG_ENABLED } from './dbgFlag';

const f = (x: number, d = 0) => (isFinite(x) ? x.toFixed(d) : '—');
const CAP = 30;
/** quantas entregas depois da primeira vista a medição da força acompanha (até a janela completa) */
const REPAINT_COMMITS = 8;
/** T ± velas para "janela completa" */
const FULL_SIDE = 1000;

interface Pt { timestamp: number; momentum?: number }

export interface TrocaReg {
  t0: number; de: string; para: string; T: number | null; caminho: string;
  pedidos: number; peso: number; falhas: number;
  tPedidos: number; tPrimeira: number; tUltima: number; tVista: number; tQuadro: number; tCompleta: number; tPreCarga: number;
  delta: number; encosto: string;
  forcaMax: number; forcaMed: number; forcaN: number;
  /** a mesma medida, mas nas entregas por vela ao vivo (não é a troca: o cálculo refeito a cada vela nova) */
  vivoMax: number;
  /** F3d C5: ms do cálculo da força na abertura (uma vez, sobre a janela inteira) e velas da janela */
  calcMs: number; velas: number;
}

class SwitchProbe {
  list: TrocaReg[] = [];
  cur: TrocaReg | null = null;
  /** força visível na primeira vista (horários e valores) e a faixa dela */
  private vT: number[] = []; private vM: number[] = []; private vRange = 0; private repaints = 0;
  private iv = 0;

  /** começa o registro de uma troca; os pedidos e respostas são anotados NESTE registro (uma carga de uma troca
   * anterior que ainda volte não entra na seguinte) */
  start(now: number, de: string, para: string, T: number | null): TrocaReg {
    const r: TrocaReg = { t0: now, de, para, T, caminho: '', pedidos: 0, peso: 0, falhas: 0, tPedidos: NaN, tPrimeira: NaN, tUltima: NaN,
      tVista: NaN, tQuadro: NaN, tCompleta: NaN, tPreCarga: NaN, delta: NaN, encosto: '', forcaMax: NaN, forcaMed: NaN, forcaN: 0, vivoMax: NaN, calcMs: NaN, velas: 0 };
    this.cur = r; this.list.push(r); if (this.list.length > CAP) this.list.shift();
    this.vT = []; this.vM = []; this.repaints = 0;
    return r;
  }
  sent(r: TrocaReg | null, now: number, caminho: string, n: number, peso: number): void {
    if (!r) return;
    r.caminho = r.caminho && r.caminho !== caminho ? `${r.caminho}+${caminho}` : caminho;
    r.pedidos += n; r.peso += peso;
    if (!(r.tPedidos >= 0)) r.tPedidos = now - r.t0;
  }
  resposta(r: TrocaReg | null, now: number, ok: boolean): void {
    if (!r || r.tVista >= 0) return;
    if (!ok) r.falhas++;
    if (!(r.tPrimeira >= 0)) r.tPrimeira = now - r.t0;
    r.tUltima = now - r.t0;
  }
  /** série nova entregue; `processed` com a vista a `d` velas do fim (a primeira vista) */
  vista(now: number, processed: readonly Pt[], d: number, ivMs: number, delta: number, encosto: string, startKnown: boolean, atPresent: boolean): void {
    const r = this.cur; if (!r) return;
    r.tVista = now - r.t0; r.delta = delta; r.encosto = encosto; this.iv = ivMs;
    const end = Math.max(Math.min(processed.length, 1000), processed.length - Math.floor(d)), st = Math.max(0, end - 1000);
    let lo = Infinity, hi = -Infinity;
    for (let i = st; i < end; i++) { const m = processed[i].momentum ?? NaN; this.vT.push(processed[i].timestamp); this.vM.push(m); if (m < lo) lo = m; if (m > hi) hi = m; }
    this.vRange = hi - lo;
    this.lastLen = processed.length;
    this.complete(now, processed, startKnown, atPresent);
  }
  /** F3d C5: o cálculo da força da abertura (antes da entrega) */
  calc(ms: number, velas: number): void { const r = this.cur; if (r && !(r.tVista >= 0)) { r.calcMs = ms; r.velas = velas; } }
  quadro(now: number): void { const r = this.cur; if (r && !(r.tQuadro >= 0)) r.tQuadro = now - r.t0; }
  /** a primeira carga (de qualquer lado, em segundo plano ou na borda) depois da primeira vista */
  precarga(now: number): void { const r = this.cur; if (r && r.tVista >= 0 && !(r.tPreCarga >= 0)) r.tPreCarga = now - r.t0; }
  /** entrega depois da primeira vista: força refeita na região visível e janela completa */
  /** `vivo` = entrega por vela ao vivo (não por carga): medida à parte (vivoMax), fora da meta da troca */
  commit(now: number, processed: readonly Pt[], startKnown: boolean, atPresent: boolean, vivo = false): void {
    const r = this.cur; if (!r || !(r.tVista >= 0)) return;
    if (this.repaints < REPAINT_COMMITS && this.vT.length && this.vRange > 0) {
      let mx = 0, sum = 0, cnt = 0, j = 0;
      for (let i = 0; i < this.vT.length; i++) {
        const ts = this.vT[i];
        while (j < processed.length && processed[j].timestamp < ts) j++;
        if (j >= processed.length || processed[j].timestamp !== ts) continue;
        const dm = Math.abs((processed[j].momentum ?? NaN) - this.vM[i]);
        if (!isFinite(dm)) continue;
        if (dm > mx) mx = dm; sum += dm; cnt++;
      }
      if (cnt > 0 && vivo) {
        this.repaints++;
        const pMax = 100 * mx / this.vRange;
        if (!(r.vivoMax >= pMax)) r.vivoMax = pMax;
      } else if (cnt > 0 && processed.length !== this.lastLen) {
        this.repaints++; r.forcaN++;
        const pMax = 100 * mx / this.vRange, pMed = 100 * sum / cnt / this.vRange;
        if (!(r.forcaMax >= pMax)) r.forcaMax = pMax;
        if (!(r.forcaMed >= pMed)) r.forcaMed = pMed;
      }
    }
    this.lastLen = processed.length;
    this.complete(now, processed, startKnown, atPresent);
  }
  private lastLen = 0;
  private complete(now: number, processed: readonly Pt[], startKnown: boolean, atPresent: boolean): void {
    const r = this.cur; if (!r || r.tCompleta >= 0) return;
    if (r.T === null) { r.tCompleta = r.tVista; return; }
    const T0 = Math.floor(r.T / (this.iv || 1)) * (this.iv || 1);
    let lo = 0, hi = processed.length;
    while (lo < hi) { const m = (lo + hi) >> 1; if (processed[m].timestamp < T0) lo = m + 1; else hi = m; }
    const older = lo, newer = processed.length - lo;
    if ((older >= FULL_SIDE || startKnown) && (newer >= FULL_SIDE || atPresent)) r.tCompleta = now - r.t0;
  }

  report(t0Abs: number, max: number): string[] {
    const L: string[] = [];
    const vs = this.list.filter(r => r.tVista >= 0).map(r => r.tVista).sort((a, b) => a - b);
    const med = vs.length ? vs[Math.floor(vs.length / 2)] : NaN, p90 = vs.length ? vs[Math.min(vs.length - 1, Math.floor(vs.length * 0.9))] : NaN;
    const fm = this.list.filter(r => r.forcaMax >= 0).map(r => r.forcaMax);
    L.push(`=== TROCAS DE SÉRIE: ${this.list.length} | até a abertura: mediana ${f(med)} ms, p90 ${f(p90)} ms | repintura da força depois da abertura (máx da região visível): ${fm.length ? f(Math.max(...fm), 1) + '%' : '—'} (meta <= 7%) ===`);
    L.push('ms desde a troca: pedidos | 1ª resposta | última | abertura (Δ velas do centro) | 1º quadro | T±1000 completa | pré-carga | cálculo da força na abertura');
    for (const r of this.list.slice(-max)) {
      const T = r.T === null ? 'presente' : new Date(r.T).toISOString().slice(0, 16).replace('T', ' ');
      L.push(`t=${f((r.t0 - t0Abs) / 1000, 1)}s ${r.de}→${r.para} ${T} | ${r.caminho || '—'} ${r.pedidos} ped. peso ${r.peso}${r.falhas ? ` falhas ${r.falhas}` : ''} | ${f(r.tPedidos)} | ${f(r.tPrimeira)} | ${f(r.tUltima)} | ${f(r.tVista)} (Δ${f(r.delta)}${r.encosto ? ' ' + r.encosto : ''}) | ${f(r.tQuadro)} | ${f(r.tCompleta)} | ${f(r.tPreCarga)} | ${r.velas ? `${f(r.calcMs)} ms/${r.velas} velas` : '—'}${r.forcaN ? ` | força depois máx ${f(r.forcaMax, 1)}% méd ${f(r.forcaMed, 1)}%` : ''}${r.vivoMax >= 0 ? ` | ao vivo máx ${f(r.vivoMax, 1)}%` : ''}`);
    }
    return L;
  }
}

export const swProbe = DBG_ENABLED ? new SwitchProbe() : null;
// replay (tests/replay): a tabela das trocas sai no relatório do harness
if (swProbe && typeof window !== 'undefined') (window as any).__swProbe = swProbe;
